/* global console */
import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";

function calculateFontSize(presetSize, globalAdd, cellAdd) {
  return parseInt(presetSize.replace("pt", "") ?? "12") + (globalAdd ?? 0) + (cellAdd ?? 0) + "pt";
}

// Shared mutation helpers so that both the individual actions and the batched
// applyStylesheet action apply identical logic to the (draft) state.
function applyPresetChange(state, payload) {
  if (payload.name) {
    Object.keys(state).forEach((k) => {
      const cellStyle = state[k];

      if (cellStyle?.preset === payload.name) {
        cellStyle.weight = payload.fontWeight ?? cellStyle.weight;
        cellStyle.style = payload.fontStyle ?? cellStyle.style;
        cellStyle.fontUnderlined =
          payload.fontUnderlined !== undefined ? payload.fontUnderlined : cellStyle.fontUnderlined;
        cellStyle.size = calculateFontSize(payload.fontSize, state.global?.fontSizeAdd, cellStyle.fontSizeAdd);
        cellStyle.fontFamily = payload.fontFamily ?? cellStyle.fontFamily;

        if (cellStyle.isInverted) {
          cellStyle.color = payload.color2 ?? cellStyle.color;
          cellStyle.colorName = payload.colorName2 ?? cellStyle.colorName;
        } else {
          cellStyle.color = payload.color ?? cellStyle.color;
          cellStyle.colorName = payload.colorName ?? cellStyle.colorName;
        }
      }
    });
  }
}

function applyColorChange(state, payload) {
  if (payload.name) {
    const { name, value } = payload;
    Object.keys(state).forEach((k) => {
      const cellStyle = state[k];

      if (cellStyle.colorName === name) {
        cellStyle.color = value;
      }
      if (cellStyle.backgroundName === name) {
        cellStyle.background = value;
      }
      if (cellStyle.iconColorName === name) {
        cellStyle.iconColor = value;
      }
      const cellShape = cellStyle.shape;
      if (cellShape) {
        if (cellShape.colorName === name) {
          cellShape.color = value;
        }
        if (cellShape.outlineColorName === name) {
          cellShape.outlineColor = value;
        }
      }
    });
  }
}

function applyShapePresetChange(state, payload) {
  if (payload.name) {
    Object.keys(state)
      .filter((k) => k !== "global" && k !== "assets" && k !== "shapeGroups")
      .forEach((k) => {
        const cs = state[k];
        if (cs?.shape?.preset === payload.name) {
          const sh = cs.shape;

          sh.type = payload.shapeType ?? sh.type;
          sh.radius = payload.radius !== undefined ? payload.radius : sh.radius;
          sh.color = payload.shapeColorValue ?? sh.color;
          sh.colorName = payload.shapeColor ?? sh.colorName;
          sh.outline = payload.outline ?? sh.outline;
          sh.outlineWeight = payload.outlineWeight ?? sh.outlineWeight;
          sh.outlineColor = payload.outlineColorValue ?? sh.outlineColor;
          sh.outlineColorName = payload.outlineColor ?? sh.outlineColorName;
          sh.shadow = payload.shadow ?? sh.shadow;

          sh.margin = payload.margin;
          sh.marginPerc = payload.marginPerc;
          sh.marginLeft = payload.marginLeft;
          sh.marginLeftPerc = payload.marginLeftPerc;
          sh.marginTop = payload.marginTop;
          sh.marginTopPerc = payload.marginTopPerc;
          sh.marginRight = payload.marginRight;
          sh.marginRightPerc = payload.marginRightPerc;
          sh.marginBottom = payload.marginBottom;
          sh.marginBottomPerc = payload.marginBottomPerc;

          cs.preset = payload.textPreset ?? cs.preset;
          cs.isInverted = payload.inverted !== undefined ? payload.inverted : cs.isInverted;
          cs.weight = payload.fontWeight ?? cs.weight;
          cs.style = payload.fontStyle ?? cs.style;
          if (payload.fontSize) {
            cs.size = calculateFontSize(payload.fontSize, state.global?.fontSizeAdd, cs.fontSizeAdd);
          }
          cs.fontFamily = payload.fontFamily ?? cs.fontFamily;
          cs.color = payload.fontColorValue ?? cs.color;
          cs.colorName = payload.fontColorName ?? cs.colorName;

          if (payload.textAlign !== undefined) cs.textAlign = payload.textAlign;
          if (payload.verticalAlign !== undefined) cs.verticalAlign = payload.verticalAlign;

          cs.padding = payload.padding !== undefined ? payload.padding : cs.padding;
          cs.paddingLeft = payload.paddingLeft !== undefined ? payload.paddingLeft : cs.paddingLeft;
          cs.paddingTop = payload.paddingTop !== undefined ? payload.paddingTop : cs.paddingTop;
          cs.paddingRight = payload.paddingRight !== undefined ? payload.paddingRight : cs.paddingRight;
          cs.paddingBottom = payload.paddingBottom !== undefined ? payload.paddingBottom : cs.paddingBottom;
          cs.paddingPerc = payload.paddingPerc !== undefined ? payload.paddingPerc : cs.paddingPerc;
        }
      });
  }
}

export const cellStylesSlice = createSlice({
  name: "cellStyles",
  initialState: {
    global: {
      fontSizeAdd: 0,
      moatColor: undefined,
      moatColorName: undefined,
    },
    assets: {},
    shapeGroups: {},
  },
  reducers: {
    updateGlobal: (state, action) => {
      if (!state.global) {
        // TODO: remove after a while, this code upgrades old Gridds with new global data
        state.global = { fontSizeAdd: 0 };
      }
      state.global.fontSizeAdd = action.payload.fontSizeAdd ?? state.global.fontSizeAdd;
      state.global.moatColor = action.payload.moatColor ?? state.global.moatColor;
      state.global.moatColorName = action.payload.moatColorName ?? state.global.moatColorName;
    },
    incrementGlobalFontSize: (state, action) => {
      if (!state.global) {
        // TODO: remove after a while, this code upgrades old Gridds with new global data
        state.global = { fontSizeAdd: 0 };
      }
      state.global.fontSizeAdd += action.payload;

      Object.keys(state)
        .filter((k) => k !== "global" && k !== "assets" && k !== "shapeGroups")
        .forEach((k) => {
          const cellStyle = state[k];
          cellStyle.size = parseInt(cellStyle.size?.replace("pt", "") ?? "12") + action.payload + "pt";
        });
    },
    incrementCellFontSize: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = {};
      }
      const cellStyle = state[action.payload.id];
      if (!cellStyle.fontSizeAdd) cellStyle.fontSizeAdd = 0;
      cellStyle.fontSizeAdd += action.payload.increment;
      cellStyle.size = parseInt(cellStyle.size?.replace("pt", "") ?? "12") + action.payload.increment + "pt";
    },
    updateStyle: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = {};
      }

      const cellStyle = state[action.payload.id];
      cellStyle.bullets = action.payload.bullets;
      cellStyle.textAlign = action.payload.textAlign ?? cellStyle.textAlign;
      cellStyle.verticalAlign = action.payload.verticalAlign ?? cellStyle.verticalAlign;
      cellStyle.weight = action.payload.weight ?? cellStyle.weight;
      cellStyle.style = action.payload.style ?? cellStyle.style;
      cellStyle.fontUnderlined =
        action.payload.fontUnderlined !== undefined ? action.payload.fontUnderlined : cellStyle.fontUnderlined;
      cellStyle.fontSizeAdd = action.payload.fontSizeAdd ?? cellStyle.fontSizeAdd;
      if (action.payload.presetSize || action.payload.fontSizeAdd) {
        cellStyle.size = calculateFontSize(
          action.payload.presetSize,
          state.global?.fontSizeAdd,
          action.payload.fontSizeAdd ?? cellStyle.fontSizeAdd
        );
      }
      cellStyle.fontFamily = action.payload.fontFamily ?? cellStyle.fontFamily;
      cellStyle.color = action.payload.color ?? cellStyle.color;
      cellStyle.colorName = action.payload.colorName ?? cellStyle.colorName;
      cellStyle.list = action.payload.list ?? cellStyle.list;
      cellStyle.isInverted = action.payload.isInverted ?? cellStyle.isInverted;

      if (
        action.payload.preset !== undefined ||
        action.payload.color ||
        action.payload.colorName ||
        action.payload.fontFamily ||
        action.payload.style ||
        action.payload.weight ||
        typeof action.payload.isInverted === "boolean" ||
        cellStyle.textAlign ||
        cellStyle.verticalAlign
      ) {
        if (cellStyle.shape) {
          cellStyle.shape.preset = undefined;
        }
      }

      if (action.payload.preset !== undefined) {
        cellStyle.preset = action.payload.preset;
      } else if (
        action.payload.color ||
        action.payload.colorName ||
        action.payload.fontFamily ||
        action.payload.style ||
        action.payload.weight
      ) {
        // TODO: refactor this to different actions instead of resetting preset all the time
        cellStyle.preset = undefined;
      }
    },
    updateTextPadding: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = {};
      }

      const cellStyle = state[action.payload.id];
      cellStyle.padding = action.payload.padding !== null ? (action.payload.padding ?? cellStyle.padding) : undefined;
      cellStyle.paddingLeft =
        action.payload.paddingLeft !== null ? (action.payload.paddingLeft ?? cellStyle.paddingLeft) : undefined;
      cellStyle.paddingTop =
        action.payload.paddingTop !== null ? (action.payload.paddingTop ?? cellStyle.paddingTop) : undefined;
      cellStyle.paddingRight =
        action.payload.paddingRight !== null ? (action.payload.paddingRight ?? cellStyle.paddingRight) : undefined;
      cellStyle.paddingBottom =
        action.payload.paddingBottom !== null ? (action.payload.paddingBottom ?? cellStyle.paddingBottom) : undefined;
      cellStyle.paddingPerc =
        action.payload.paddingPerc !== null ? (action.payload.paddingPerc ?? cellStyle.paddingPerc) : undefined;
      cellStyle.paddingLeftPerc =
        action.payload.paddingLeftPerc !== null
          ? (action.payload.paddingLeftPerc ?? cellStyle.paddingLeftPerc)
          : undefined;
      cellStyle.paddingTopPerc =
        action.payload.paddingTopPerc !== null
          ? (action.payload.paddingTopPerc ?? cellStyle.paddingTopPerc)
          : undefined;
      cellStyle.paddingRightPerc =
        action.payload.paddingRightPerc !== null
          ? (action.payload.paddingRightPerc ?? cellStyle.paddingRightPerc)
          : undefined;
      cellStyle.paddingBottomPerc =
        action.payload.paddingBottomPerc !== null
          ? (action.payload.paddingBottomPerc ?? cellStyle.paddingBottomPerc)
          : undefined;
      if (cellStyle.shape) cellStyle.shape.preset = undefined;
    },
    updateBackground: (state, action) => {
      const {
        payload: { id, background, backgroundName },
      } = action;
      if (!id) {
        return;
      }
      if (state[id] === undefined) {
        state[id] = {};
      }

      const cellStyle = state[id];
      cellStyle.background = background ?? cellStyle.background;
      cellStyle.backgroundName = backgroundName ?? cellStyle.backgroundName;
    },
    removeBackground: (state, action) => {
      if (state[action.payload.id] === undefined) {
        return;
      }
      const cellStyle = state[action.payload.id];
      cellStyle.background = undefined;
      cellStyle.backgroundName = undefined;
    },
    updateShape: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = { shape: {} };
      }

      if (state[action.payload.id].shape === undefined) {
        state[action.payload.id].shape = {};
      }

      const cellShape = state[action.payload.id].shape;
      cellShape.type = action.payload.type ?? cellShape.type;
      cellShape.color = action.payload.color ?? cellShape.color;
      cellShape.colorName = action.payload.colorName ?? cellShape.colorName;
      cellShape.radius = action.payload.radius ?? cellShape.radius;
      cellShape.outline = action.payload.outline ?? cellShape.outline;
      cellShape.outlineWeight = action.payload.outlineWeight ?? cellShape.outlineWeight;
      cellShape.outlineColor = action.payload.outlineColor ?? cellShape.outlineColor;
      cellShape.outlineColorName = action.payload.outlineColorName ?? cellShape.outlineColorName;
      cellShape.shadow = action.payload.shadow ?? cellShape.shadow;
      if (action.payload.preset !== undefined) {
        cellShape.preset = action.payload.preset;
      } else if (action.payload.type || action.payload.color || action.payload.outline !== undefined) {
        cellShape.preset = undefined;
      }
    },
    updateShapeMargin: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = { shape: {} };
      }

      if (state[action.payload.id].shape === undefined) {
        state[action.payload.id].shape = {};
      }

      const cellShape = state[action.payload.id].shape;
      cellShape.margin = action.payload.margin !== null ? (action.payload.margin ?? cellShape.margin) : undefined;
      cellShape.marginPerc =
        action.payload.marginPerc !== null ? (action.payload.marginPerc ?? cellShape.marginPerc) : undefined;
      cellShape.marginLeft =
        action.payload.marginLeft !== null ? (action.payload.marginLeft ?? cellShape.marginLeft) : undefined;
      cellShape.marginTop =
        action.payload.marginTop !== null ? (action.payload.marginTop ?? cellShape.marginTop) : undefined;
      cellShape.marginRight =
        action.payload.marginRight !== null ? (action.payload.marginRight ?? cellShape.marginRight) : undefined;
      cellShape.marginBottom =
        action.payload.marginBottom !== null ? (action.payload.marginBottom ?? cellShape.marginBottom) : undefined;
      cellShape.marginLeftPerc =
        action.payload.marginLeftPerc !== null
          ? (action.payload.marginLeftPerc ?? cellShape.marginLeftPerc)
          : undefined;
      cellShape.marginTopPerc =
        action.payload.marginTopPerc !== null ? (action.payload.marginTopPerc ?? cellShape.marginTopPerc) : undefined;
      cellShape.marginRightPerc =
        action.payload.marginRightPerc !== null
          ? (action.payload.marginRightPerc ?? cellShape.marginRightPerc)
          : undefined;
      cellShape.marginBottomPerc =
        action.payload.marginBottomPerc !== null
          ? (action.payload.marginBottomPerc ?? cellShape.marginBottomPerc)
          : undefined;

      cellShape.zIndex = action.payload.zIndex !== null ? (action.payload.zIndex ?? cellShape.zIndex) : undefined;
      cellShape.preset = undefined;
    },
    updateShapeTransform: (state, action) => {
      if (state[action.payload.id] === undefined || state[action.payload.id].shape === undefined) {
        return;
      }

      const cellShape = state[action.payload.id].shape;
      cellShape.rotation = action.payload.rotation ?? cellShape.rotation;
      cellShape.flipX = action.payload.flipX ?? cellShape.flipX;
      cellShape.flipY = action.payload.flipY ?? cellShape.flipY;
      cellShape.preset = undefined;
    },
    transformShape: (state, action) => {
      if (state[action.payload.id] === undefined) {
        return;
      }

      if (state[action.payload.id].shape === undefined) {
        return;
      }

      const cellShape = state[action.payload.id].shape;
      if (cellShape.rotation === undefined || cellShape.rotation === null) {
        cellShape.rotation = 0;
      }
      cellShape.rotation += action.payload.rotation ?? 0;
      if (cellShape.rotation >= 360) cellShape.rotation -= 360;
      cellShape.flipX = action.payload.flipX ? !(cellShape.flipX ?? false) : cellShape.flipX;
      cellShape.flipY = action.payload.flipY ? !(cellShape.flipY ?? false) : cellShape.flipY;
      cellShape.preset = undefined;
    },
    removeShape: (state, action) => {
      if (state[action.payload.id]?.shape) {
        state[action.payload.id].shape = undefined;
      }
      if (state[action.payload.id]?.shapeGroupId) {
        const groupId = state[action.payload.id].shapeGroupId;
        Object.keys(state)
          .filter((k) => k !== "global" && k !== "assets" && k !== "shapeGroups")
          .forEach((k) => {
            const cellStyle = state[k];
            if (cellStyle.shapeGroupId === groupId) {
              cellStyle.shapeGroupId = undefined;
              cellStyle.shape = undefined;
            }
          });
        state.shapeGroups[groupId] = undefined;
      }
    },
    reset: (state, action) => {
      return { ...action.payload };
    },
    resetOne: (state, action) => {
      if (action.payload.id) state[action.payload.id] = { ...action.payload, id: undefined };
    },
    presetChanged: (state, action) => {
      applyPresetChange(state, action.payload);
    },
    applyShapePreset: (state, action) => {
      const { id } = action.payload;
      if (!state[id]) state[id] = {};
      if (!state[id].shape) state[id].shape = {};
      const cs = state[id];
      const sh = cs.shape;

      sh.preset = action.payload.name;
      sh.type = action.payload.shapeType;
      sh.radius = action.payload.radius;
      sh.color = action.payload.shapeColorValue;
      sh.colorName = action.payload.shapeColor;
      sh.outline = action.payload.outline;
      sh.outlineWeight = action.payload.outlineWeight;
      sh.outlineColor = action.payload.outlineColorValue;
      sh.outlineColorName = action.payload.outlineColor;
      sh.shadow = action.payload.shadow ?? 0;
      sh.zIndex = 1;

      sh.margin = action.payload.margin;
      sh.marginPerc = action.payload.marginPerc;
      sh.marginLeft = action.payload.marginLeft;
      sh.marginLeftPerc = action.payload.marginLeftPerc;
      sh.marginTop = action.payload.marginTop;
      sh.marginTopPerc = action.payload.marginTopPerc;
      sh.marginRight = action.payload.marginRight;
      sh.marginRightPerc = action.payload.marginRightPerc;
      sh.marginBottom = action.payload.marginBottom;
      sh.marginBottomPerc = action.payload.marginBottomPerc;

      cs.preset = action.payload.textPreset;
      cs.isInverted = action.payload.inverted;
      cs.weight = action.payload.fontWeight;
      cs.style = action.payload.fontStyle;
      cs.size = calculateFontSize(action.payload.fontSize, state.global?.fontSizeAdd, cs.fontSizeAdd);
      cs.fontFamily = action.payload.fontFamily;
      cs.color = action.payload.fontColorValue;
      cs.colorName = action.payload.fontColorName;

      if (action.payload.textAlign !== undefined) cs.textAlign = action.payload.textAlign;
      if (action.payload.verticalAlign !== undefined) cs.verticalAlign = action.payload.verticalAlign;

      cs.padding = action.payload.padding;
      cs.paddingLeft = action.payload.paddingLeft;
      cs.paddingTop = action.payload.paddingTop;
      cs.paddingRight = action.payload.paddingRight;
      cs.paddingBottom = action.payload.paddingBottom;
      cs.paddingPerc = action.payload.paddingPerc;
    },
    shapePresetChanged: (state, action) => {
      applyShapePresetChange(state, action.payload);
    },
    setIcon: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = {};
      }

      const cellStyle = state[action.payload.id];
      cellStyle.icon = action.payload.icon?.id;

      if (action.payload.icon?.id) {
        cellStyle.iconAlign = action.payload.iconAlign ?? cellStyle.iconAlign;
        cellStyle.iconSize = action.payload.iconSize ?? cellStyle.iconSize;
        cellStyle.iconEnclosure = action.payload.iconEnclosure ?? cellStyle.iconEnclosure;
        cellStyle.iconColor = action.payload.iconColor ?? cellStyle.iconColor;
        cellStyle.iconColorName = action.payload.iconColorName ?? cellStyle.iconColorName;
        cellStyle.iconRatio = action.payload.iconRatio ?? cellStyle.iconRatio;
      }
    },
    alignIcon: (state, action) => {
      if (state[action.payload.id] === undefined) {
        return;
      }

      const cellStyle = state[action.payload.id];
      cellStyle.iconAlign = action.payload.iconAlign ?? cellStyle.iconAlign;
      cellStyle.iconSize = action.payload.iconSize ?? cellStyle.iconSize;
      cellStyle.iconEnclosure = action.payload.iconEnclosure ?? cellStyle.iconEnclosure;
      if (action.payload.iconRatio !== undefined) cellStyle.iconRatio = action.payload.iconRatio;
    },
    updateIconMargin: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = { iconStyle: {} };
      }

      if (state[action.payload.id].iconStyle === undefined) {
        state[action.payload.id].iconStyle = {};
      }

      const cellIcon = state[action.payload.id].iconStyle;
      cellIcon.margin = action.payload.margin !== null ? (action.payload.margin ?? cellIcon.margin) : undefined;
      cellIcon.marginPerc =
        action.payload.marginPerc !== null ? (action.payload.marginPerc ?? cellIcon.marginPerc) : undefined;
      cellIcon.marginLeft =
        action.payload.marginLeft !== null ? (action.payload.marginLeft ?? cellIcon.marginLeft) : undefined;
      cellIcon.marginTop =
        action.payload.marginTop !== null ? (action.payload.marginTop ?? cellIcon.marginTop) : undefined;
      cellIcon.marginRight =
        action.payload.marginRight !== null ? (action.payload.marginRight ?? cellIcon.marginRight) : undefined;
      cellIcon.marginBottom =
        action.payload.marginBottom !== null ? (action.payload.marginBottom ?? cellIcon.marginBottom) : undefined;
      cellIcon.marginLeftPerc =
        action.payload.marginLeftPerc !== null ? (action.payload.marginLeftPerc ?? cellIcon.marginLeftPerc) : undefined;
      cellIcon.marginTopPerc =
        action.payload.marginTopPerc !== null ? (action.payload.marginTopPerc ?? cellIcon.marginTopPerc) : undefined;
      cellIcon.marginRightPerc =
        action.payload.marginRightPerc !== null
          ? (action.payload.marginRightPerc ?? cellIcon.marginRightPerc)
          : undefined;
      cellIcon.marginBottomPerc =
        action.payload.marginBottomPerc !== null
          ? (action.payload.marginBottomPerc ?? cellIcon.marginBottomPerc)
          : undefined;

      cellIcon.zIndex = action.payload.zIndex !== null ? (action.payload.zIndex ?? cellIcon.zIndex) : undefined;
    },
    setIconColor: (state, action) => {
      if (state[action.payload.id] === undefined) {
        return;
      }

      const cellStyle = state[action.payload.id];
      cellStyle.iconColor = action.payload.color;
      cellStyle.iconColorName = action.payload.colorName;
    },
    swap: (state, action) => {
      console.log("swapping styles", action);
      if (action.payload.cells?.length === 2) {
        const first = action.payload.cells[0];
        const second = action.payload.cells[1];

        if (state[first.id].shapeGroupId || state[second.id].shapeGroupId) {
          console.log("Can't swap shape group styles! Skipping style swap!", { first, second });
          return;
        }

        const temp = state[first.id];
        state[first.id] = state[second.id];
        state[second.id] = temp;
      }
    },
    setCustomImage: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = {};
      }

      if (action.payload.img) {
        if (state[action.payload.id].img?.assetLink) {
          state.assets[state[action.payload.id].img.assetLink] = undefined;
        }
        state[action.payload.id].img = action.payload.img;
      }
    },
    clearCustomImage: (state, action) => {
      if (state[action.payload.id] !== undefined) {
        if (state[action.payload.id].img?.assetLink) {
          state.assets[state[action.payload.id].img.assetLink] = undefined;
        }
        state[action.payload.id].img = undefined;
      }
    },
    editImage: (state, action) => {
      if (action.payload.id === undefined) {
        return;
      }

      const img = state[action.payload.id].img;
      if (img.assetLink === undefined) {
        if (state.assets === undefined) {
          state.assets = {};
        }
        const assetId = uuid();
        state.assets[assetId] = { ...img };
        img.assetLink = assetId;
      }
      img.content = action.payload.img.content;
      img.params = { ...action.payload.img.params };
    },
    updateImage: (state, action) => {
      if (state[action.payload.id] === undefined) {
        state[action.payload.id] = { img: {} };
      }

      if (state[action.payload.id].img === undefined) {
        state[action.payload.id].img = {};
      }

      const cellImage = state[action.payload.id].img;
      cellImage.margin = action.payload.margin !== null ? (action.payload.margin ?? cellImage.margin) : undefined;
      cellImage.marginPerc =
        action.payload.marginPerc !== null ? (action.payload.marginPerc ?? cellImage.marginPerc) : undefined;
      cellImage.marginLeft =
        action.payload.marginLeft !== null ? (action.payload.marginLeft ?? cellImage.marginLeft) : undefined;
      cellImage.marginTop =
        action.payload.marginTop !== null ? (action.payload.marginTop ?? cellImage.marginTop) : undefined;
      cellImage.marginRight =
        action.payload.marginRight !== null ? (action.payload.marginRight ?? cellImage.marginRight) : undefined;
      cellImage.marginBottom =
        action.payload.marginBottom !== null ? (action.payload.marginBottom ?? cellImage.marginBottom) : undefined;
      cellImage.marginLeftPerc =
        action.payload.marginLeftPerc !== null
          ? (action.payload.marginLeftPerc ?? cellImage.marginLeftPerc)
          : undefined;
      cellImage.marginTopPerc =
        action.payload.marginTopPerc !== null ? (action.payload.marginTopPerc ?? cellImage.marginTopPerc) : undefined;
      cellImage.marginRightPerc =
        action.payload.marginRightPerc !== null
          ? (action.payload.marginRightPerc ?? cellImage.marginRightPerc)
          : undefined;
      cellImage.marginBottomPerc =
        action.payload.marginBottomPerc !== null
          ? (action.payload.marginBottomPerc ?? cellImage.marginBottomPerc)
          : undefined;

      cellImage.zIndex = action.payload.zIndex !== null ? (action.payload.zIndex ?? cellImage.zIndex) : undefined;
    },
    restoreImage: (state, action) => {
      if (action.payload.id === undefined) {
        return;
      }

      const img = state[action.payload.id].img;
      if (img?.assetLink !== undefined) {
        const asset = state.assets[img.assetLink];
        img.content = asset.content;
        img.type = asset.type;
      }
    },
    colorChanged: (state, action) => {
      applyColorChange(state, action.payload);
    },
    // Applies an entire stylesheet (presets, colors, status colors, shape
    // presets) in a single reducer pass — one state update, one re-render,
    // one undo entry — instead of dispatching once per entry. Border presets
    // are handled by bordersSlice via extraReducers on this same action.
    applyStylesheet: (state, action) => {
      const sheet = action.payload;
      if (!sheet) {
        return;
      }
      const findColor = (colorName) => sheet.colors?.find((c) => c.name === colorName)?.value;

      sheet.presets?.forEach((p) => {
        applyPresetChange(state, {
          ...p,
          color: findColor(p.fontColor) ?? "black",
          colorName: p.fontColor,
          color2: findColor(p.fontColor2) ?? "white",
          colorName2: p.fontColor2,
        });
      });

      sheet.colors?.forEach((c) => applyColorChange(state, c));
      sheet.statusColors?.forEach((sc) => applyColorChange(state, sc));

      sheet.shapePresets?.forEach((sp) => {
        const textPreset = sheet.presets?.find((p) => p.name === sp.textPreset);
        const fontColorKey = sp.inverted ? textPreset?.fontColor2 : textPreset?.fontColor;
        applyShapePresetChange(state, {
          ...sp,
          shapeColorValue: findColor(sp.shapeColor),
          outlineColorValue: findColor(sp.outlineColor),
          fontWeight: textPreset?.fontWeight,
          fontStyle: textPreset?.fontStyle,
          fontSize: textPreset?.fontSize,
          fontFamily: textPreset?.fontFamily,
          fontColorValue: findColor(fontColorKey),
          fontColorName: fontColorKey,
        });
      });
    },
    copyStyle: (state, action) => {
      // from one id to another id to copy full style
      console.log("copying style between cells", { ...action.payload, sourceStyle: state[action.payload.sourceId] });
      const style = state[action.payload.sourceId] ?? action.payload.fallbackStyle;
      state[action.payload.targetId] = {
        ...style,
        img: undefined,
        //icon: undefined,
      };
    },
    cleanData: (state, action) => {
      if (action.payload.cells) {
        // TODO: clear styles that belong to ids not mentioned in cells matrix
      }

      const ids = Object.keys(state);
      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        const shape = state[id].shape;
        if (shape?.transparency) {
          shape.transparency = undefined;
        }
        if (shape?.size) {
          shape.size = undefined;
        }
      }
    },
    updateShapeGroup: (state, action) => {
      if (!state.shapeGroups) {
        state.shapeGroups = {};
      }
      if (!state.shapeGroups[action.payload.id]) {
        state.shapeGroups[action.payload.id] = {};
      }

      const group = state.shapeGroups[action.payload.id];
      group.type = action.payload.type ?? group.type;
      group.fill = action.payload.fill ?? group.fill;
      group.fillName = action.payload.fillName ?? group.fillName;
      group.outline = action.payload.outline ?? group.outline;
      group.outlineWeight = action.payload.outlineWeight ?? group.outlineWeight;
      group.outlineColor = action.payload.outlineColor ?? group.outlineColor;
      group.outlineColorName = action.payload.outlineColorName ?? group.outlineColorName;
      group.gap = action.payload.gap ?? group.gap;
      group.margin = action.payload.margin ?? group.margin;
      group.reverse = action.payload.reverse !== undefined ? action.payload.reverse : group.reverse;

      if (action.payload.cells && action.payload.cells.length > 0) {
        Object.keys(state)
          .filter((k) => k !== "global" && k !== "assets" && k !== "shapeGroups")
          .forEach((cellStyle) => {
            if (cellStyle.shapeGroupId === action.payload.id) {
              cellStyle.shapeGroupId = undefined;
              if (cellStyle.shape) cellStyle.shape.type = undefined;
            }
          });

        Object.keys(state)
          .filter((k) => action.payload.cells.includes(k))
          .forEach((k) => {
            const cellStyle = state[k];
            cellStyle.shapeGroupId = action.payload.id;
            if (cellStyle.shape) cellStyle.shape.type = undefined;
            else cellStyle.shape = {};
            cellStyle.shape.color = action.payload.fill ?? cellStyle.color;
            cellStyle.shape.colorName = action.payload.fillName ?? cellStyle.colorName;
            cellStyle.shape.outline = action.payload.outline ?? cellStyle.outline;
            cellStyle.shape.outlineWeight = action.payload.outlineWeight ?? cellStyle.outlineWeight;
            cellStyle.shape.outlineColor = action.payload.outlineColor ?? cellStyle.outlineColor;
            cellStyle.shape.outlineColorName = action.payload.outlineColorName ?? cellStyle.outlineColorName;
          });
      }
    },
    removeShapeGroup: (state, action) => {
      Object.keys(state)
        .filter((k) => k !== "global" && k !== "assets" && k !== "shapeGroups")
        .forEach((k) => {
          const cellStyle = state[k];
          if (cellStyle.shapeGroupId === action.payload) {
            cellStyle.shapeGroupId = undefined;
            cellStyle.shape = undefined;
          }
        });
      state.shapeGroups[action.payload] = undefined;
    },
  },
});

export const {
  reset,
  resetOne,
  updateGlobal,
  incrementGlobalFontSize,
  incrementCellFontSize,
  updateStyle,
  updateTextPadding,
  updateBackground,
  removeBackground,
  updateShape,
  updateShapeMargin,
  updateShapeTransform,
  transformShape,
  removeShape,
  presetChanged,
  applyShapePreset,
  shapePresetChanged,
  setIcon,
  alignIcon,
  updateIconMargin,
  setIconColor,
  swap,
  setCustomImage,
  clearCustomImage,
  editImage,
  updateImage,
  restoreImage,
  colorChanged,
  applyStylesheet,
  copyStyle,
  updateShapeGroup,
  removeShapeGroup,
} = cellStylesSlice.actions;

export default cellStylesSlice.reducer;
